"use strict";

/**
 * HeaderMenu
 *
 * Handles desktop navigation disclosure (details/summary) with accessibility support.
 * Features:
 * - Works only on lg-up (1024px+) screens
 * - Closes on focus out of the disclosure
 * - Closes when clicking overlay (.p-navi__spacer)
 * - Handles screen size changes properly
 * - No aria-expanded (using native details element)
 * - Treats as link list (no keyboard navigation support)
 */
class HeaderMenu {
  constructor() {
    this.isLargeScreen = false;
    this.activeDisclosure = null;
    this.boundHandlers = {
      documentClick: this.handleDocumentClick.bind(this),
      focusOut: this.handleFocusOut.bind(this),
      breakpointChange: this.handleBreakpointChange.bind(this),
    };

    this.init();
  }

  init() {
    window.addEventListener(
      "breakpoint-lg-up",
      this.boundHandlers.breakpointChange
    );

    if (window.isDesktop) {
      this.isLargeScreen = window.isDesktop();
      this.bindEvents();
    }
  }

  bindEvents() {
    if (this.isLargeScreen) {
      this.addEventListeners();
    } else {
      this.closeAll();
    }
  }

  addEventListeners() {
    document.addEventListener("click", this.boundHandlers.documentClick);
    document.addEventListener("focusout", this.boundHandlers.focusOut);

    const details = document.querySelectorAll("details.header-menu-dropdown");
    details.forEach((detail) => {
      detail.addEventListener("toggle", this.handleDetailsToggle.bind(this));
    });
  }

  handleDetailsToggle(e) {
    const details = e.currentTarget;

    if (!details) return;

    // Close other open disclosures
    this.closeOthers(details);

    // Toggle body class for overlay
    if (details.open) {
      document.body.classList.add("isNavigationOpen");
      this.activeDisclosure = details;
    } else {
      document.body.classList.remove("isNavigationOpen");
      this.activeDisclosure = null;
    }
  }

  handleFocusOut(e) {
    if (!this.activeDisclosure) return;

    const details = this.activeDisclosure;
    const relatedTarget = e.relatedTarget;

    // Check if focus is moving outside the disclosure
    if (!relatedTarget || !details.contains(relatedTarget)) {
      this.closeDisclosure(details);
      document.body.classList.remove("isNavigationOpen");
    }
  }

  handleDocumentClick(e) {
    // Close navigation when clicking on the overlay spacer
    if (e.target.matches(".p-navi__spacer")) {
      this.closeAll();
      document.body.classList.remove("isNavigationOpen");
    }
  }

  handleBreakpointChange(e) {
    this.isLargeScreen = e.detail.matches;
    this.bindEvents();
  }

  closeDisclosure(details) {
    if (details && details.open) {
      details.open = false;
      if (this.activeDisclosure === details) {
        this.activeDisclosure = null;
      }
    }
  }

  closeOthers(exceptDetails) {
    const allDisclosures = document.querySelectorAll(".nav_list-item details");
    allDisclosures.forEach((details) => {
      if (details !== exceptDetails) {
        this.closeDisclosure(details);
      }
    });
  }

  closeAll() {
    const allDisclosures = document.querySelectorAll(".nav_list-item details");
    allDisclosures.forEach((details) => {
      this.closeDisclosure(details);
    });
    this.activeDisclosure = null;
  }

  destroy() {
    document.removeEventListener("click", this.boundHandlers.documentClick);
    document.removeEventListener("focusout", this.boundHandlers.focusOut);
    window.removeEventListener(
      "breakpoint-lg-up",
      this.boundHandlers.breakpointChange
    );
    this.closeAll();
    document.body.classList.remove("isNavigationOpen");
  }
}

function initHeaderMenu() {
  if (typeof window.headerMenu !== "undefined") {
    window.headerMenu.destroy();
  }
  window.headerMenu = new HeaderMenu();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initHeaderMenu);
} else {
  initHeaderMenu();
}

/**
 * Drawer
 *
 * Handles mobile navigation drawer functionality with accessibility support.
 * Features:
 * - Focus management and trap
 * - Keyboard navigation (ESC key)
 * - Expandable submenus
 * - Click outside to close
 * - Proper ARIA attributes
 */
const Drawer = {
  config: {
    drawerClass: "is-screen-on",
    buttonClass: "is-active",
    bodyClass: "has-drawer",
    animationDuration: 300,
  },

  element: null,
  triggers: [],
  isOpen: false,
  lastFocusedElement: null,
  escListenerAdded: false,
  drawerFocusTrap: null,

  init(drawerId) {
    this.element = document.getElementById(drawerId);
    if (!this.element) {
      console.error(`Drawer element not found: #${drawerId}`);
      return this;
    }

    this.triggers = Array.from(
      document.querySelectorAll(`[aria-controls="${drawerId}"]`)
    );

    if (this.triggers.length === 0) {
      console.warn(`No triggers found for drawer: #${drawerId}`);
    }

    this.setupExpandables();
    this.setupTriggers();
    this.setupEscKey();
    this.updateState(false);

    this.drawerFocusTrap = window.focusTrap.createFocusTrap(this.element, {
      escapeDeactivates: true,
      clickOutsideDeactivates: true,
      allowOutsideClick: true,
    });

    return this;
  },

  setupExpandables() {
    this.expandableMenus = this.element.querySelectorAll("details");
    this.expandableMenus.forEach((details) => {
      details.addEventListener("toggle", () => {
        if (details.open) {
          details.classList.add("active");
        } else {
          details.classList.remove("active");
        }
        const navItems = this.element.querySelectorAll(
          ".nav_list-item > a, .nav_list-item > details > summary"
        );
        navItems.forEach((el) => {
          if (details.open && !details.contains(el)) {
            el.setAttribute("tabindex", "-1");
          } else {
            el.removeAttribute("tabindex");
          }
        });
      });
      if (details.open) {
        details.classList.add("active");
      } else {
        details.classList.remove("active");
      }
      const navItems = this.element.querySelectorAll(
        ".nav_list-item > a, .nav_list-item > details > summary"
      );
      navItems.forEach((el) => {
        if (details.open && !details.contains(el)) {
          el.setAttribute("tabindex", "-1");
        } else {
          el.removeAttribute("tabindex");
        }
      });
      const closeBtn = details.querySelector(".menu-drawer__close-button");
      if (closeBtn) {
        closeBtn.addEventListener("click", function (e) {
          details.open = false;
        });
      }
    });
  },

  setupTriggers() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        this.lastFocusedElement = e.currentTarget;
        this.toggle();
      });
    });
  },

  setupEscKey() {
    if (this.escListenerAdded) return;

    this.escListenerAdded = true;
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });
  },

  open() {
    if (this.isOpen) return;
    this.updateState(true);
  },

  close() {
    if (!this.isOpen) return;
    this.updateState(false);
  },

  toggle() {
    this.updateState(!this.isOpen);
  },

  updateState(open) {
    this.isOpen = open;

    this.element.classList.toggle(this.config.drawerClass, open);

    this.triggers.forEach((trigger) => {
      trigger.classList.toggle(this.config.buttonClass, open);
      trigger.setAttribute("aria-expanded", open);

      const label = open
        ? trigger.dataset.labelClose
        : trigger.dataset.labelOpen;
      if (label) {
        trigger.setAttribute("aria-label", label);
      }
    });

    document.documentElement.classList.toggle(this.config.bodyClass, open);

    this.manageFocus(open);

    if (!open && this.expandableMenus) {
      this.expandableMenus.forEach((details) => {
        details.open = false;
      });
    }

    if (this.drawerFocusTrap) {
      setTimeout(() => {
        if (open) {
          this.drawerFocusTrap.activate();
        } else {
          this.drawerFocusTrap.deactivate();
        }
      }, this.config.animationDuration);
    }

    this.element.dispatchEvent(
      new CustomEvent(open ? "drawer:open" : "drawer:close", {
        bubbles: true,
        detail: { drawer: this },
      })
    );
  },

  manageFocus(open) {
    setTimeout(() => {
      if (open) {
        const focusableElements = this.element.querySelectorAll(
          'a, summary, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      } else if (
        this.lastFocusedElement &&
        document.contains(this.lastFocusedElement)
      ) {
        this.lastFocusedElement.focus();
      }
    }, this.config.animationDuration);
  },
};

window.Drawer = Drawer;

// Initialize Drawer when DOM is ready
function initDrawer() {
  Drawer.init("global-navi-drawer");
  const spacer = document.querySelector("div.p-global-navi__spacer");
  if (spacer) {
    spacer.addEventListener("click", () => {
      Drawer.close();
    });
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initDrawer);
} else {
  initDrawer();
}
